import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardMedia,
  Skeleton,
  Breadcrumbs,
  Link as MuiLink,
  Rating,
  TextField,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Trash2 } from 'lucide-react';
import { getProducts } from '../firebase/products';
import { getCategories } from '../firebase/categories';
import { addReview, deleteReview, getProductReviews } from '../firebase/reviews';
import { Product, Category, Review, TimePrice } from '../types';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../firebase/auth';
import toast from 'react-hot-toast';

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [newReview, setNewReview] = useState({
    name: '',
    rating: 5,
    comment: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [selectedTimePrice, setSelectedTimePrice] = useState<TimePrice | null>(null);

  const addToCart = useCartStore((state) => state.addItem);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (id) {
      loadProduct();
      loadReviews();
    }
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const [products, categories] = await Promise.all([
        getProducts(),
        getCategories(),
      ]);
      
      const foundProduct = products.find(p => p.id === id);
      if (!foundProduct) {
        setError('Producto no encontrado');
        return;
      }

      setProduct(foundProduct);
      const foundCategory = categories.find(c => c.id === foundProduct.categoryId);
      setCategory(foundCategory || null);
      
      const primaryImage = foundProduct.images.find(img => img.isPrimary);
      if (primaryImage) {
        setSelectedImage(primaryImage.url);
      }

      // Set default time price if available
      if (foundProduct.timePrices?.length > 0) {
        setSelectedTimePrice(foundProduct.timePrices[0]);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      setError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!id) return;
    try {
      setReviewsLoading(true);
      const reviewsData = await getProductReviews(id);
      const sortedReviews = [...reviewsData].sort((a, b) => 
        b.createdAt.getTime() - a.createdAt.getTime()
      );
      setReviews(sortedReviews);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Error al cargar las reseñas');
    } finally {
      setReviewsLoading(false);
    }
  };

  const calculateDiscountedPrice = (price: number, discountPercentage?: number | null) => {
    if (!discountPercentage) return price;
    return price * (1 - discountPercentage / 100);
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, selectedTimePrice);
      toast.success('Producto añadido al carrito');
    }
  };

  const handleSubmitReview = async () => {
    setReviewError(null);
    
    if (!newReview.name.trim()) {
      setReviewError('Por favor, introduce tu nombre');
      return;
    }
    if (!newReview.comment.trim()) {
      setReviewError('Por favor, escribe un comentario');
      return;
    }
    if (!id) return;

    try {
      await addReview({
        productId: id,
        name: newReview.name,
        rating: newReview.rating,
        comment: newReview.comment,
      });
      
      await loadReviews();
      setNewReview({ name: '', rating: 5, comment: '' });
      setOpenDialog(false);
      toast.success('Reseña añadida correctamente');
    } catch (error) {
      console.error('Error adding review:', error);
      toast.error('Error al añadir la reseña');
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      await deleteReview(reviewId);
      await loadReviews();
      toast.success('Reseña eliminada correctamente');
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Error al eliminar la reseña');
    }
  };

  const averageRating = reviews.length > 0
    ? reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length
    : 0;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              {[1, 2, 3].map((n) => (
                <Skeleton key={n} variant="rectangular" width={80} height={80} />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton height={40} width="80%" />
            <Skeleton height={24} width="40%" sx={{ mt: 2 }} />
            <Skeleton height={100} sx={{ mt: 2 }} />
            <Skeleton height={56} width={200} sx={{ mt: 4 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Producto no encontrado'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Breadcrumbs sx={{ mb: 4 }}>
        <MuiLink component={Link} to="/" color="inherit">
          Inicio
        </MuiLink>
        <MuiLink
          component={Link}
          to={`/categoria/${category?.name.toLowerCase()}`}
          color="inherit"
        >
          {category?.name}
        </MuiLink>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardMedia
              component="img"
              height="400"
              image={selectedImage || product.images[0]?.url}
              alt={product.name}
              sx={{ objectFit: 'contain' }}
            />
          </Card>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            {product.images.map((image) => (
              <Card
                key={image.url}
                sx={{
                  width: 80,
                  cursor: 'pointer',
                  border: selectedImage === image.url ? '2px solid #4F46E5' : 'none',
                }}
                onClick={() => setSelectedImage(image.url)}
              >
                <CardMedia
                  component="img"
                  height="80"
                  image={image.url}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontFamily: 'Orbitron, sans-serif',
              fontWeight: 700,
              color: '#ffffff',
              textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
            }}
          >
            {product.name}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating value={averageRating} precision={0.5} readOnly />
            <Typography sx={{ fontFamily: 'Rajdhani, sans-serif', color: '#aaaaaa' }}>
              ({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})
            </Typography>
          </Box>

          {product.shortDescription && (
            <Box 
              sx={{ 
                mt: 3,
                fontFamily: 'Rajdhani, sans-serif',
                color: '#e0e0e0',
                fontSize: '1.1rem'
              }}
              dangerouslySetInnerHTML={{ __html: product.shortDescription }}
            />
          )}

          {product.timePrices?.length > 0 ? (
            <Box sx={{ mt: 4 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#00ffff' }}>Seleccionar Tiempo</InputLabel>
                <Select
                  value={selectedTimePrice?.id || ''}
                  label="Seleccionar Tiempo"
                  onChange={(e) => {
                    const timePrice = product.timePrices.find(tp => tp.id === e.target.value);
                    setSelectedTimePrice(timePrice || null);
                  }}
                  sx={{
                    color: '#ffffff',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00ffff',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00ffff',
                    },
                    '.MuiSvgIcon-root': {
                      color: '#00ffff',
                    }
                  }}
                >
                  {product.timePrices.map((tp) => {
                    const originalPrice = tp.price;
                    const discountedPrice = calculateDiscountedPrice(tp.price, product.discountPercentage);
                    
                    return (
                      <MenuItem key={tp.id} value={tp.id}>
                        {tp.time} - {product.discountPercentage ? (
                          <>
                            <span style={{ textDecoration: 'line-through', marginRight: 8 }}>
                              {originalPrice}€
                            </span>
                            {discountedPrice.toFixed(2)}€
                          </>
                        ) : (
                          `${originalPrice}€`
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              
              {selectedTimePrice && (
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mt: 2,
                    color: '#00ffff',
                    fontFamily: 'Orbitron, sans-serif',
                    textShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
                  }}
                >
                  {product.discountPercentage && (
                    <span style={{ 
                      textDecoration: 'line-through', 
                      marginRight: '1rem',
                      color: '#ff0055',
                      fontSize: '0.8em',
                      textShadow: 'none'
                    }}>
                      {selectedTimePrice.price}€
                    </span>
                  )}
                  {calculateDiscountedPrice(
                    selectedTimePrice.price,
                    product.discountPercentage
                  ).toFixed(2)}€
                </Typography>
              )}
            </Box>
          ) : (
            <Typography 
              variant="h4" 
              sx={{ 
                mt: 3,
                color: '#00ffff',
                fontFamily: 'Orbitron, sans-serif',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
              }}
            >
              {product.discountPercentage && (
                <span style={{ 
                  textDecoration: 'line-through', 
                  marginRight: '1rem',
                  color: '#ff0055',
                  fontSize: '0.8em',
                  textShadow: 'none'
                }}>
                  {product.price}€
                </span>
              )}
              {calculateDiscountedPrice(
                product.price,
                product.discountPercentage
              ).toFixed(2)}€
            </Typography>
          )}
          
          {product.discountPercentage && (
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#ffffff',
                mt: 1,
                display: 'inline-block',
                bgcolor: 'rgba(255, 0, 85, 0.6)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontFamily: 'Orbitron, sans-serif',
                boxShadow: '0 0 10px rgba(255, 0, 85, 0.4)'
              }}
            >
              -{product.discountPercentage}% descuento
            </Typography>
          )}
          
          <Button
            variant="contained"
            size="large"
            startIcon={<ShoppingCart />}
            sx={{ 
              mt: 4,
              bgcolor: 'rgba(0, 255, 255, 0.1)',
              color: '#00ffff',
              border: '1px solid #00ffff',
              fontFamily: 'Orbitron, sans-serif',
              '&:hover': {
                bgcolor: 'rgba(0, 255, 255, 0.2)',
                boxShadow: '0 0 15px rgba(0, 255, 255, 0.4)',
              },
              '&:disabled': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
            onClick={handleAddToCart}
            disabled={product.timePrices?.length > 0 && !selectedTimePrice}
          >
            Añadir al Carrito
          </Button>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {product.description && (
            <Box 
              sx={{ 
                mt: 3,
                fontFamily: 'Rajdhani, sans-serif',
                color: '#e0e0e0',
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          )}
        </Grid>
      </Grid>

      {/* Reviews Section */}
      <Box sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h2"
            className="font-orbitron"
            sx={{
              color: '#ffffff',
              textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
              fontFamily: 'Orbitron, sans-serif',
              fontWeight: 700,
            }}
          >
            Reseñas
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Star />}
            onClick={() => setOpenDialog(true)}
            sx={{
              color: '#00ffff',
              borderColor: '#00ffff',
              fontFamily: 'Orbitron, sans-serif',
              '&:hover': {
                borderColor: '#ffffff',
                color: '#ffffff',
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
                boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
              },
            }}
          >
            Escribir Reseña
          </Button>
        </Box>

        {reviewsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#00ffff' }} />
          </Box>
        ) : reviews.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 4, fontFamily: 'Rajdhani, sans-serif', color: '#aaaaaa', fontSize: '1.2rem' }}>
            Sé el primero en dejar una reseña
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {reviews.map((review) => (
              <Grid item xs={12} key={review.id}>
                <Card sx={{ 
                  p: 3,
                  background: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 255, 255, 0.1)',
                  boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Orbitron, sans-serif', color: '#ffffff' }}>
                        {review.name}
                      </Typography>
                      <Rating value={review.rating} readOnly size="small" />
                      <Typography sx={{ mt: 1, fontFamily: 'Rajdhani, sans-serif', color: '#aaaaaa' }}>
                        {review.createdAt.toLocaleDateString()}
                      </Typography>
                    </Box>
                    {user && (
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteReview(review.id)}
                      >
                        <Trash2 size={20} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography sx={{ mt: 2, fontFamily: 'Rajdhani, sans-serif', color: '#e0e0e0', fontSize: '1.1rem' }}>
                    {review.comment}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Review Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            border: '1px solid #00ffff',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Orbitron, sans-serif', color: '#ffffff' }}>Escribir Reseña</DialogTitle>
        <DialogContent>
          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reviewError}
            </Alert>
          )}
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Nombre"
              value={newReview.name}
              onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
              InputLabelProps={{ sx: { color: '#aaaaaa' } }}
              InputProps={{
                sx: {
                  color: '#ffffff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00ffff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00ffff' },
                }
              }}
            />
            <Box>
              <Typography component="legend" sx={{ color: '#ffffff', fontFamily: 'Rajdhani, sans-serif' }}>Valoración</Typography>
              <Rating
                value={newReview.rating}
                onChange={(_, value) => setNewReview({ ...newReview, rating: value || 5 })}
              />
            </Box>
            <TextField
              fullWidth
              label="Comentario"
              multiline
              rows={4}
              value={newReview.comment}
              onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
              InputLabelProps={{ sx: { color: '#aaaaaa' } }}
              InputProps={{
                sx: {
                  color: '#ffffff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00ffff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00ffff' },
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: '#aaaaaa' }}>Cancelar</Button>
          <Button 
            onClick={handleSubmitReview} 
            variant="contained"
            sx={{
              bgcolor: '#00ffff',
              color: '#000000',
              fontFamily: 'Orbitron, sans-serif',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#ffffff',
              }
            }}
          >
            Publicar Reseña
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductDetail;
